import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Toast } from './toast.types';

@Component({
  selector: 'sh-toast',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="flex items-center justify-between cursor-pointer gap-3 rounded-xl border px-4 py-3 shadow-sm w-full"
      [ngClass]="containerClass"
      (click)="closed.emit(toast.id)"
      (mouseenter)="paused.emit(toast.id)"
      (mouseleave)="resumed.emit(toast.id)"
    >
      <div class="flex items-center gap-3">
        <span
          class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
          [ngClass]="iconBgClass"
        >
          <ng-container [ngSwitch]="toast.type">
            <svg
              *ngSwitchCase="'success'"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="white"
              class="h-3 w-3"
            >
              <path
                fill-rule="evenodd"
                d="M16.7 5.3a1 1 0 010 1.4l-7.4 7.4a1 1 0 01-1.4 0L3.3 9.5a1 1 0 111.4-1.4l3.9 3.9 6.7-6.7a1 1 0 011.4 0z"
                clip-rule="evenodd"
              />
            </svg>
            <svg
              *ngSwitchCase="'info'"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="white"
              class="h-3 w-3"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-9a1 1 0 112 0v4a1 1 0 11-2 0V9zm1-4a1 1 0 100 2 1 1 0 000-2z"
                clip-rule="evenodd"
              />
            </svg>
            <svg
              *ngSwitchCase="'error'"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="white"
              class="h-3 w-3"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.7 7.3a1 1 0 011.4 0L10 7.6l.3-.3a1 1 0 111.4 1.4l-.3.3.3.3a1 1 0 11-1.4 1.4l-.3-.3-.3.3a1 1 0 11-1.4-1.4l.3-.3-.3-.3a1 1 0 010-1.4z"
                clip-rule="evenodd"
              />
            </svg>
          </ng-container>
        </span>
        <span class="text-sm font-medium" [ngClass]="textClass">{{ toast.message }}</span>
      </div>
      <button type="button" class="shrink-0 text-gray-500 hover:text-gray-700">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          class="h-4 w-4"
        >
          <path
            d="M6.4 5L5 6.4 8.6 10 5 13.6 6.4 15 10 11.4 13.6 15 15 13.6 11.4 10 15 6.4 13.6 5 10 8.6 6.4 5z"
          />
        </svg>
      </button>
    </div>
  `,
})
export class ToastComponent {
  @Input({ required: true }) toast!: Toast;
  @Output() closed = new EventEmitter<number>();
  @Output() paused = new EventEmitter<number>();
  @Output() resumed = new EventEmitter<number>();

  get containerClass(): string {
    switch (this.toast.type) {
      case 'success':
        return 'bg-green-50 border-green-100';
      case 'info':
        return 'bg-indigo-50 border-indigo-100';
      case 'error':
        return 'bg-red-50 border-red-100';
    }
  }

  get iconBgClass(): string {
    switch (this.toast.type) {
      case 'success':
        return 'bg-green-600';
      case 'info':
        return 'bg-indigo-600';
      case 'error':
        return 'bg-red-500';
    }
  }

  get textClass(): string {
    switch (this.toast.type) {
      case 'success':
        return 'text-green-700';
      case 'info':
        return 'text-indigo-700';
      case 'error':
        return 'text-red-600';
    }
  }
}
